import { readFileSync, writeFileSync, mkdirSync } from 'fs';
import { join } from 'path';
import { homedir } from 'os';

const CONFIG_DIR = join(homedir(), '.config', 'rekor');
const CONFIG_FILE = join(CONFIG_DIR, 'config.json');

// A freshly-installed CLI talks to production by default. Local development opts out
// with REKOR_API_URL=http://localhost:8787 (the dev knob, per CLAUDE.md).
const DEFAULT_API_URL = 'https://api.rekor.pro';

export interface Config {
  api_url: string;
  default_database?: string;
  org_id?: string;
  /**
   * Report-action nudge dedup keys already shown to the user, so the login/status reminder fires
   * once per notifiable event. Shipped keys are `<report_id>:shipped:<contest_count>` (re-nudges on
   * a re-shipped fix, matching the re-firing shipped email); dismissed keys are
   * `<report_id>:dismissed` (once-ever). Capped to the most-recent entries by the writer.
   */
  nudged_report_ids?: string[];
}

interface ConfigFile extends Config {
  // Tokens may linger here from older CLI versions until token-store migrates them.
  token?: string;
  [key: string]: unknown;
}

export function loadConfig(): Config {
  const envUrl = process.env['REKOR_API_URL'];

  try {
    const raw = readFileSync(CONFIG_FILE, 'utf-8');
    const stored = JSON.parse(raw) as ConfigFile;
    return {
      api_url: envUrl ?? stored.api_url ?? DEFAULT_API_URL,
      default_database: stored.default_database,
      org_id: stored.org_id,
      nudged_report_ids: Array.isArray(stored.nudged_report_ids)
        ? stored.nudged_report_ids.filter((x: unknown): x is string => typeof x === 'string')
        : undefined,
    };
  } catch {
    return {
      api_url: envUrl ?? DEFAULT_API_URL,
    };
  }
}

export function saveConfig(config: Config): void {
  mkdirSync(CONFIG_DIR, { recursive: true, mode: 0o700 });
  let existing: ConfigFile = {} as ConfigFile;
  try {
    existing = JSON.parse(readFileSync(CONFIG_FILE, 'utf-8')) as ConfigFile;
  } catch {
    existing = {} as ConfigFile;
  }
  const merged: ConfigFile = { ...existing, ...config };
  writeFileSync(CONFIG_FILE, JSON.stringify(merged, null, 2), { mode: 0o600 });
}

export { CONFIG_DIR, CONFIG_FILE };
